import React, { createContext, useContext } from 'react';
import { useFilterActions } from './ActionsProvider';
import { useFilterState, useAvailableFilterState } from './StateProvider';
import { fromSummaryDiffToChanges, useDiff } from '../../../Shared/summaryDiff';
import {
  FilterSummary,
  FilterChangesContextType,
  FilterSummaryState,
  FilterChangesProviderProps,
  FilterSummaryProviderProps,
} from './types';
import { useTransportTypeProduct } from '../../TransportTypeFilter/Provider';

const AppliedFilterContext = createContext<boolean>(false);
const FilterSummaryContext = createContext<FilterSummary[]>([]);
const FilterChangesContext = createContext<FilterChangesContextType>([]);

const createFilterSummary = (
  selectedConnectionThresholds: string[],
  availableConnectionThresholds: { resetValue: string }[]
): FilterSummaryState => {
  if (
    !selectedConnectionThresholds ||
    selectedConnectionThresholds.length === 0
  ) {
    return {
      summary: [],
      isApplied: false,
    };
  }

  const calculateSelectedConnectionThresholdSummary = (
    selectedConnectionThreshold: string,
    resetValue: string
  ): FilterSummary => {
    if (!selectedConnectionThreshold) {
      return {
        selected: resetValue,
        isApplied: false,
      };
    }
    const isApplied = selectedConnectionThreshold !== resetValue;
    return {
      selected: selectedConnectionThreshold,
      isApplied,
    };
  };

  const summary: FilterSummary[] = [];
  let isApplied = false;

  selectedConnectionThresholds.forEach((selectedConnectionThreshold, legId) => {
    const resetValue = availableConnectionThresholds[legId].resetValue;
    const connectionThresholdSummary =
      calculateSelectedConnectionThresholdSummary(
        selectedConnectionThreshold,
        resetValue
      );

    isApplied = isApplied || connectionThresholdSummary.isApplied;

    summary.push(connectionThresholdSummary);
  });

  return {
    summary,
    isApplied,
  };
};

const FilterChangesProvider: React.FC<FilterChangesProviderProps> = ({
  summary,
  children,
}) => {
  const { previous, next } = useDiff(summary);
  const { resetLeg } = useFilterActions();
  const changes = fromSummaryDiffToChanges(
    previous,
    next,
    'maxStops',
    resetLeg
  );
  return (
    <FilterChangesContext.Provider value={changes}>
      {children}
    </FilterChangesContext.Provider>
  );
};

const FilterSummaryProvider: React.FC<FilterSummaryProviderProps> = ({
  children,
}) => {
  const selectedConnectionThresholds = useFilterState();
  const availableConnectionThresholds = useAvailableFilterState();
  const { product } = useTransportTypeProduct();

  const availableForType = Array.isArray(
    availableConnectionThresholds?.[product]
  )
    ? availableConnectionThresholds[product]
    : [];

  const selectedForType = Array.isArray(selectedConnectionThresholds?.[product])
    ? selectedConnectionThresholds[product]
    : [];

  const { summary, isApplied } = createFilterSummary(
    selectedForType,
    availableForType
  );
  return (
    <FilterSummaryContext.Provider value={summary}>
      <FilterChangesProvider summary={summary}>
        <AppliedFilterContext.Provider value={isApplied}>
          {children}
        </AppliedFilterContext.Provider>
      </FilterChangesProvider>
    </FilterSummaryContext.Provider>
  );
};

export const useFilterSummary = (): FilterSummary[] =>
  useContext(FilterSummaryContext);

export const useFilterChanges = (): FilterChangesContextType =>
  useContext(FilterChangesContext);

export const useAppliedFilter = (): boolean => useContext(AppliedFilterContext);

export default FilterSummaryProvider;
